/**
 * Permit card — the office's answer, per device (Issue #1250).
 *
 * A permit number used to block the TEST: the field app refused to leave
 * Initial Conditions on a Garland "New" install without one. A tech cannot
 * produce a number the city has not issued yet, so the rule stopped work.
 *
 * The permit is answered HERE instead, and it gates the JOB CLOSING. One row
 * per device, because a backflow can require its own permit for each device —
 * with a "one permit covers all" shortcut at the top, because the common case
 * is a single permit for the visit and that must stay a single action.
 *
 * Rendered only when this job's jurisdiction actually asks for a permit; a job
 * in a city with no rule never sees the card at all.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

export interface PermitDevice {
    testId: string;
    assetId: string;
    deviceLabel: string;
    reasonForTest: string;
    permitNumber: string | null;
    noPermitPulled: boolean;
    answeredAt: string | null;
    answeredByName: string | null;
}

export interface PermitState {
    applies: boolean;
    providerName: string | null;
    devices: PermitDevice[];
    unanswered: PermitDevice[];
    notRequired: Array<{
        assetId: string;
        deviceLabel: string;
        reasonForTest: string;
        why: "reason_not_covered" | "predates_rule";
    }>;
}

const REASON_LABEL: Record<string, string> = {
    new: "New install",
    replacement: "Replacement",
    existing: "Yearly test",
};

function answered(d: PermitDevice): boolean {
    return d.noPermitPulled || Boolean((d.permitNumber ?? "").trim());
}

function stamp(d: PermitDevice): string | null {
    if (!d.answeredAt) return null;
    const when = new Date(d.answeredAt).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
    // The caller prefixes "Entered by " / "Marked by ", so a nameless answer
    // must not return a bare date — that renders "Entered by Aug 26, 2026".
    // Migration 560's backfill deliberately stamps a time with no actor, and
    // `users.first_name` defaults to '', so this is reachable.
    return d.answeredByName ? `${d.answeredByName} · ${when}` : `someone in the office · ${when}`;
}

export function PermitCard({ jobId, canManage }: { jobId: string; canManage: boolean }) {
    const qc = useQueryClient();
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [allNumber, setAllNumber] = useState("");
    const [showAll, setShowAll] = useState(false);

    const permitQ = useQuery({
        queryKey: ["job", jobId, "permit"],
        queryFn: () => api.get<{ permit: PermitState }>(`/api/jobs/${jobId}/permit`),
    });
    const state = permitQ.data?.permit;

    const answer = useMutation({
        mutationFn: (body: { assetIds: string[]; permitNumber?: string | null; noPermitPulled?: boolean }) =>
            api.post<{ permit: PermitState }>(`/api/jobs/${jobId}/permit`, body),
        onSuccess: (res, vars) => {
            qc.setQueryData(["job", jobId, "permit"], res);
            // The job's own gate state changed — anything showing "can this
            // close?" must not keep showing the stale answer.
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            setDrafts((d) => {
                const next = { ...d };
                for (const id of vars.assetIds) delete next[id];
                return next;
            });
            setAllNumber("");
            setShowAll(false);
            toast.success(
                vars.noPermitPulled
                    ? `Marked no permit on ${vars.assetIds.length} device${vars.assetIds.length === 1 ? "" : "s"}.`
                    : `Permit saved on ${vars.assetIds.length} device${vars.assetIds.length === 1 ? "" : "s"}.`,
            );
        },
        onError: (e) => {
            toast.error(
                e instanceof ApiError && typeof e.body === "object" && e.body
                    ? ((e.body as { message?: string }).message ?? "Couldn't save the permit answer.")
                    : "Couldn't save the permit answer.",
            );
        },
    });

    // A city with no permit rule never shows this card.
    if (permitQ.isLoading || !state?.applies) return null;

    const left = state.unanswered.length;
    const city = state.providerName ? ` for ${state.providerName}` : "";

    return (
        <div className="rounded-lg border bg-card p-4" data-testid="permit-card">
            <div className="mb-1 flex items-center gap-2">
                <h3 className="text-sm font-semibold">Permit numbers</h3>
                {left === 0 ? (
                    <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">
                        <CheckCircle2 className="mr-1 h-3 w-3" /> All answered
                    </Badge>
                ) : (
                    <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
                        <AlertTriangle className="mr-1 h-3 w-3" /> {left} to answer
                    </Badge>
                )}
            </div>

            {left > 0 ? (
                <p className="mb-3 text-xs text-muted-foreground">
                    This job can&apos;t close until every device below is answered
                    {city}. Put the number in whenever it comes through — the tests are already done and filed as far as
                    they can go.
                </p>
            ) : (
                <p className="mb-3 text-xs text-muted-foreground">
                    Every device is answered{city}. Nothing here is holding the job.
                </p>
            )}

            {/* One permit for the whole visit is the common case — keep it one action. */}
            {canManage && left > 1 && (
                <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-3 dark:border-sky-800/50 dark:bg-sky-950/30">
                    {!showAll ? (
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="min-w-[12rem] flex-1 text-xs font-medium text-sky-900 dark:text-sky-200">
                                {left === 2 ? "One permit for both?" : `One permit covers all ${left}?`}
                                <span className="block font-normal text-sky-800/80 dark:text-sky-300/80">
                                    Enter it once and it fills every device below.
                                </span>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => setShowAll(true)}>
                                Use one permit
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-2">
                            <Input
                                autoFocus
                                className="min-w-[10rem] flex-1"
                                placeholder="Permit number for all devices"
                                value={allNumber}
                                onChange={(e) => setAllNumber(e.target.value)}
                            />
                            <Button
                                size="sm"
                                disabled={!allNumber.trim() || answer.isPending}
                                onClick={() =>
                                    answer.mutate({
                                        assetIds: state.unanswered.map((d) => d.assetId),
                                        permitNumber: allNumber.trim(),
                                    })
                                }
                            >
                                {answer.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                                {left === 2 ? "Use it on both" : `Save for all ${left}`}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setShowAll(false)}>
                                Cancel
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <div className="space-y-2">
                {state.devices.map((d) => {
                    const isAnswered = answered(d);
                    const draft = drafts[d.assetId] ?? "";
                    return (
                        <div
                            key={d.assetId}
                            data-testid={`permit-row-${d.assetId}`}
                            className={
                                isAnswered
                                    ? "rounded-md border border-emerald-200 bg-emerald-50/50 p-3 dark:border-emerald-800/40 dark:bg-emerald-950/20"
                                    : "rounded-md border p-3"
                            }
                        >
                            <div className="mb-2 flex flex-wrap items-baseline gap-2">
                                <span className="text-sm font-semibold">{d.deviceLabel}</span>
                                <Badge variant="secondary" className="text-[10px]">
                                    {REASON_LABEL[d.reasonForTest] ?? d.reasonForTest}
                                </Badge>
                            </div>

                            {isAnswered ? (
                                <div className="flex flex-wrap items-center gap-2 text-sm">
                                    {d.noPermitPulled ? (
                                        <Badge variant="outline">No permit pulled</Badge>
                                    ) : (
                                        <span className="font-semibold">{d.permitNumber}</span>
                                    )}
                                    {canManage && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() =>
                                                setDrafts((s) => ({ ...s, [d.assetId]: d.permitNumber ?? "" }))
                                            }
                                        >
                                            Change
                                        </Button>
                                    )}
                                    {stamp(d) && (
                                        <span className="w-full text-xs text-muted-foreground">
                                            {d.noPermitPulled ? "Marked by " : "Entered by "}
                                            {stamp(d)}
                                        </span>
                                    )}
                                </div>
                            ) : null}

                            {canManage && (!isAnswered || drafts[d.assetId] !== undefined) && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <Input
                                        className="min-w-[10rem] flex-1"
                                        placeholder="e.g. GRL-2026-04417"
                                        value={draft}
                                        onChange={(e) => setDrafts((s) => ({ ...s, [d.assetId]: e.target.value }))}
                                    />
                                    <Button
                                        size="sm"
                                        disabled={!draft.trim() || answer.isPending}
                                        onClick={() =>
                                            answer.mutate({ assetIds: [d.assetId], permitNumber: draft.trim() })
                                        }
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={answer.isPending}
                                        onClick={() => answer.mutate({ assetIds: [d.assetId], noPermitPulled: true })}
                                    >
                                        No permit
                                    </Button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* STATES ALWAYS SPEAK: a device the office remembers testing but
                that needs no permit is NAMED, so nobody hunts for a row that
                was never coming. */}
            {state.notRequired.length > 0 && (
                <div className="mt-3 rounded-md bg-muted/60 p-2 text-xs text-muted-foreground">
                    {/* ONE lead-in, then a clause per reason. Repeating "One
                        device isn't listed:" for each group read like a bug. */}
                    <span className="font-medium text-foreground">
                        {state.notRequired.length === 1
                            ? "One device isn't listed"
                            : `${state.notRequired.length} devices aren't listed`}
                        :
                    </span>{" "}
                    {(["reason_not_covered", "predates_rule"] as const)
                        .map((why) => {
                            const group = state.notRequired.filter((d) => d.why === why);
                            if (group.length === 0) return null;
                            const names = group.map((d) => d.deviceLabel).join(", ");
                            const one = group.length === 1;
                            return why === "reason_not_covered"
                                ? `${names} — ${one ? "a yearly test" : "yearly tests"} on ${one ? "a device" : "devices"} that ${one ? "was" : "were"} already there, so no permit is wanted`
                                : `${names} — done before this rule started`;
                        })
                        .filter(Boolean)
                        .join("; ")}
                    {state.notRequired.length === 1
                        ? ". It can't hold the job up."
                        : ". None of them can hold the job up."}
                </div>
            )}
        </div>
    );
}
